import { useEffect, useMemo, useState } from "react";
import { TemplateDto } from "../dto/template";

export type TemplateVars = Record<string, string>;

const normalizeNewlines = (s: string) => (s ?? "").replace(/\r\n/g, "\n");

const toInputType = (t?: string) => {
  switch (t) {
    case "number":
      return "number";
    case "date":
      return "date";
    case "time":
      return "time";
    case "datetime":
      return "datetime-local";
    case "string":
    default:
      return "text";
  }
};

const PLACEHOLDER_RE = /{{\s*([^}]+)\s*}}/g;

function isNumericKey(key: string) {
  return /^[0-9]+$/.test(key.trim());
}

export function useTemplateOnlyComposer(templates: TemplateDto[]) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [vars, setVars] = useState<TemplateVars>({});

  useEffect(() => {
    if (selectedId) return;
    if (templates?.length) setSelectedId(templates[0].id);
  }, [templates, selectedId]);

  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId]
  );

  useEffect(() => {
    setVars({});
  }, [selectedId]);

  const variables = selected?.variables ?? [];

  const setVar = (key: string, value: string) => {
    setVars((prev) => ({ ...prev, [key]: value }));
  };

  const contentTokens = useMemo(() => {
    const raw = normalizeNewlines(selected?.content ?? "");
    const tokens = new Set<string>();

    for (const m of raw.matchAll(PLACEHOLDER_RE)) {
      const token = (m[1] ?? "").trim();
      if (token) tokens.add(token);
    }

    return tokens;
  }, [selected?.content]);

  // Variabili “attive” = quelle dichiarate nel DTO e usate nel content
  //    (ordine = ordine dell’array variables, NON ordine del content)
  const activeVariables = useMemo(() => {
    if (!variables.length) return [];
    if (!contentTokens.size) return variables; // fallback: se non trovi token, mostra tutte
    return variables.filter((v) => contentTokens.has((v.key ?? "").trim()));
  }, [variables, contentTokens]);

  // Match SEMPRE per key, mai per indice
  const getVarByToken = (tokenRaw: string) => {
    const token = tokenRaw.trim();
    if (!token) return null;

    const idx = variables.findIndex((x) => (x.key ?? "").trim() === token);
    if (idx < 0) return null;

    const v = variables[idx];
    return v ? { v, idx } : null;
  };

  // requiredKeys basato sulle variabili realmente richieste (activeVariables)
  const requiredKeys = useMemo(
    () => activeVariables.map((v) => v.key),
    [activeVariables]
  );

  const hasMissingVars = useMemo(() => {
    if (!activeVariables.length) return false;
    return requiredKeys.some((k) => !(vars[k] ?? "").toString().trim().length);
  }, [requiredKeys, vars, activeVariables.length]);

  const renderedContentString = useMemo(() => {
    const raw = normalizeNewlines(selected?.content ?? "");
    if (!raw) return "";

    return raw.replace(PLACEHOLDER_RE, (_m, tokenRaw) => {
      const match = getVarByToken(tokenRaw);
      if (!match) return "";
      const key = match.v.key;
      return (vars[key] ?? "").toString();
    });
  }, [selected?.content, vars, variables]);

  const resetVars = () => setVars({});

  const previewNodes = useMemo(() => {
    const raw = normalizeNewlines(selected?.content ?? "");
    if (!raw) return null;

    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;

    const matches = Array.from(raw.matchAll(PLACEHOLDER_RE));
    for (let i = 0; i < matches.length; i++) {
      const m = matches[i];
      const full = m[0];
      const tokenRaw = m[1] ?? "";
      const start = m.index ?? 0;
      const end = start + full.length;

      if (start > lastIndex) nodes.push(raw.slice(lastIndex, start));

      const match = getVarByToken(tokenRaw);

      if (!match) {
        nodes.push(full);
      } else {
        const key = match.v.key;
        const type = match.v.type;
        const value = (vars[key] ?? "").toString();

        // ✅ Se key è "1" "2" ecc -> label più leggibile
        const label = isNumericKey(key)
          ? `Inserisci valore`
          : `Inserisci ${key}`;

        nodes.push(
          value.trim().length ? (
            value
          ) : (
            <b key={`missing-${key}-${i}`}>
              {label}
              {type ? ` (${type})` : ""}
            </b>
          )
        );
      }

      lastIndex = end;
    }

    if (lastIndex < raw.length) nodes.push(raw.slice(lastIndex));
    return nodes;
  }, [selected?.content, vars, variables]);

  // ✅ Inputs: ordine = activeVariables (quindi ordine del DTO, non content)
  const inputs = useMemo(() => {
    return activeVariables.map((v, index) => {
      const key = v.key;

      const label = isNumericKey(key) ? `Inserisci valore` : `Inserisci ${key}`;

      return {
        key,
        label,
        type: v.type,
        inputType: toInputType(v.type),
        value: (vars[key] ?? "").toString(),
      };
    });
  }, [activeVariables, vars]);

  return {
    selectedId,
    setSelectedId,
    selected,
    vars,
    setVar,
    inputs,
    hasMissingVars,
    previewNodes,
    renderedContentString,
    normalizeNewlines,
    resetVars,
  };
}
